import { useMemo, useState, useCallback } from 'react'
import type React from 'react'
import { Button } from '@/components/ui/button'
import { Trash2 } from 'lucide-react'
import { Status, RoleChoice, RoomMember } from '@/redux/types/room'
import OGEAvatar from '../OGE/OGEAvatar'
import { SERVER_URL } from '@/api/config'
import { UserRole } from '@/constants/room'
import { useTeamName } from '@/hooks/useTeamName'
import { cn } from '@/lib/utils'
import { ResendInviteDialog } from '@/components/InviteMembers/ResendInviteDialog'

const TeamDisplay = ({ teamId }: { teamId: number }) => {
  const teamName = useTeamName(teamId)
  return <span className='text-xs text-gray-500'>Team: {teamName}</span>
}

interface MemberListProps {
  members: RoomMember[]
  userRole: UserRole
  title: string
  isGameStarted: boolean
  isLoading: boolean
  resendLoading: Record<string, boolean>
  onResend: (memberEmail: string, customMessage?: string) => Promise<void>
  onSendInvite: (memberEmail: string) => Promise<void>
  onDelete: (playerId: string, playerEmail: string) => void
  showTeamInfo?: boolean
  headerAction?: React.ReactNode
  roomTemplate?: string | null
}

export function MemberList({
  members,
  userRole,
  title,
  isGameStarted,
  isLoading,
  resendLoading,
  onResend,
  onSendInvite,
  onDelete,
  showTeamInfo = false,
  headerAction,
  roomTemplate,
}: MemberListProps) {
  const [resendDialogOpen, setResendDialogOpen] = useState(false)
  const [selectedMember, setSelectedMember] = useState<RoomMember | null>(null)

  const filteredMembers = useMemo(() => {
    return members.filter((member) => {
      if (userRole === UserRole.Student) {
        return member.role === RoleChoice.PLAYER
      } else {
        return member.role === RoleChoice.CO_PROFESSOR
      }
    })
  }, [members, userRole])

  const handleResendClick = useCallback(
    (memberEmail: string) => {
      const member = members.find((m) => m.member_email === memberEmail)
      if (member) {
        setSelectedMember(member)
        setResendDialogOpen(true)
      }
    },
    [members]
  )

  const handleResendConfirm = useCallback(
    async (customMessage: string) => {
      if (selectedMember) {
        await onResend(selectedMember.member_email, customMessage)
        setResendDialogOpen(false)
        setSelectedMember(null)
      }
    },
    [selectedMember, onResend]
  )

  if (filteredMembers.length === 0) {
    return null
  }

  return (
    <div className='flex-wrap w-full space-y-4'>
      <div className='flex items-center justify-between gap-2'>
        <h3 className='text-sm font-medium text-dark-blue'>{title}</h3>
        {headerAction && <div className='flex-shrink-0'>{headerAction}</div>}
      </div>
      <div className='space-y-3 max-h-60 overflow-y-auto pr-2'>
        {filteredMembers.map((member) => (
          <div
            key={member.gamealias}
            className='flex flex-wrap items-center justify-between w-full gap-2'
          >
            <div className='flex flex-col'>
              <div className='flex items-center gap-2'>
                <OGEAvatar
                  src={
                    member?.member_profile_picture
                      ? SERVER_URL + member?.member_profile_picture
                      : null
                  }
                  fallback={member?.member_name?.substring(0, 2).toUpperCase()}
                  variant='md'
                  isLoading={isLoading}
                />

                <div className='flex flex-col'>
                  <span
                    className={cn(
                      'text-sm',
                      member.invitation_status === Status.Accepted
                        ? 'text-dark-blue'
                        : 'text-[#6D6E71]'
                    )}
                  >
                    {member.member_email}
                  </span>

                  <div className='flex flex-col'>
                    {showTeamInfo ? (
                      member.teamId ? (
                        <TeamDisplay teamId={member.teamId} />
                      ) : (
                        <span className='text-xs text-gray-500'>
                          No team assigned yet
                        </span>
                      )
                    ) : (
                      <span className='text-xs text-gray-500'>
                        Role: {member.role}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className='flex gap-1'>
              {member.invitation_status === Status.Pending &&
                !isGameStarted && (
                  <Button
                    variant='secondary'
                    size='sm'
                    className='bg-[#008BC0] hover:bg-[#0076c0] text-white'
                    onClick={() => handleResendClick(member.member_email)}
                    disabled={resendLoading[member.gamealias]}
                  >
                    {resendLoading[member.gamealias]
                      ? 'Sending...'
                      : 'Resend Invite'}
                  </Button>
                )}
              {member.invitation_status === Status.Draft && !isGameStarted && (
                <Button
                  variant='secondary'
                  size='sm'
                  className='bg-[#008BC0] hover:bg-[#0076c0] text-white'
                  onClick={() => onSendInvite(member.member_email)}
                >
                  {resendLoading[member.gamealias]
                    ? 'Sending...'
                    : 'Send Invite'}
                </Button>
              )}
              {!isGameStarted && (
                <Button
                  className='w-8 h-8 bg-dark-blue hover:bg-dark-blue-hovered'
                  onClick={() =>
                    onDelete(member.member_email, member.member_email)
                  }
                >
                  <Trash2 className='w-4 h-4' />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {selectedMember && (
        <ResendInviteDialog
          isOpen={resendDialogOpen}
          onClose={() => {
            setResendDialogOpen(false)
            setSelectedMember(null)
          }}
          onSend={handleResendConfirm}
          memberEmail={selectedMember.member_email}
          roomTemplate={roomTemplate}
          playerName={selectedMember.member_name || selectedMember.member_email}
        />
      )}
    </div>
  )
}
